import {Component} from '@angular/core';
import {NoticeCardComponent} from './notice-card.component';
import {NotifyService} from './notify.service';

/**
 * The single notification surface for the console.
 *
 * Mounted once, in AppComponent, so every module shares one stack in one
 * place — no module needs its own toast markup, position or z-index, and two
 * modules can never disagree about where a message appears.
 *
 * Nothing is passed in: it reads NotifyService's signal, so any code that can
 * inject the service can raise a message from anywhere.
 */
@Component({
  standalone: true,
  imports: [NoticeCardComponent],
  selector: 'app-notify-host',
  templateUrl: './notify-host.component.html',
  styleUrl: './notify-host.component.css'
})
export class NotifyHostComponent {
  /** pointer is over the stack — freezes every card's countdown bar */
  paused = false;

  constructor(readonly notify: NotifyService) {
  }

  /**
   * The bar and the dismiss timer have to stop together, or the card vanishes
   * under the cursor with its countdown still showing time left.
   *
   * Only a pointer that can actually hover is allowed to pause. A touch fires
   * `pointerenter` on any tap that lands on the stack — which, under 640px, is
   * the full width of the screen just below the header — and its matching
   * `pointerleave` can go missing entirely when the row is dismissed or times
   * out under the finger. That latched the pause on for the rest of the
   * session, and `NotifyService.arm()` gives a notice raised while paused no
   * live timer at all: every toast after the first stayed on screen forever.
   * That is the "toast never disappears on mobile" report, and it was never
   * specific to Internal Notes — it just needs one stray tap on the stack.
   */
  hold(event?: PointerEvent): void {
    if (event?.pointerType === 'touch') {
      return;
    }
    this.paused = true;
    this.notify.hold();
  }

  /** Never gated on pointer type: dropping a pause is always the safe direction. */
  release(): void {
    this.paused = false;
    this.notify.resume();
  }
}
